import { dayjs } from '@karve.it/core';
import {
    createFeature,
    createReducer,
    on,
} from '@ngrx/store';

import { cloneDeep } from 'lodash';
import { PrimeIcons } from 'primeng/api';
import { CallState, LoadingState } from 'src/app/utilities/state.util';

import { v4 as uuid } from 'uuid';

import {
    Asset,
    Attendee,
    CalendarEventForDispatchFragment,
    CalendarEventForScheduleFragment,
    JobUser,
    Role,
    User
} from '../../../../generated/graphql.generated';

import { EventAttendeeRoles, eventAttendeeRolesArray } from '../../../global.constants';

import { defaultCrewSettings } from '../components/dispatch-crew-settings/default-crew-settings';

import { DefaultBlockEntry, DispatchActions } from './dispatch.actions';
import { AUTO_GENERATED_META_KEY, AUTO_GENERATED_META_VALUE } from './dispatch.default-blocks';

// States that are tracked by the job tool under "callState"
export const DISPATCH_CALL_STATES = [
    'events',
    'users',
    'roles',
    'assets',
    'updateCrew',
    'assignCrewWithAI',
    'crewScheduleSend',
    'sendScheduleSubmit',
] as const;

/** Send status for a crew member in the Send Schedule dialog */
export type CrewScheduleSendStatus = 'not-sent' | 'up-to-date' | 'changed' | 'removed';

/**
 * OPS task fields → flat metadata keys (the exact shape SavePendingChanges
 * persists and the crew email builder reads). Values must be primitives per
 * the Neo4j metadata rule.
 */
export function opsTaskFieldsToMetadata(fields: Record<string, any>): Record<string, string | number | boolean> {
    const meta: Record<string, string | number | boolean> = {};
    if (fields.clientName !== undefined) meta.clientName = String(fields.clientName);
    if (fields.clientPhone !== undefined) meta.clientPhone = String(fields.clientPhone);
    if (fields.clientEmail !== undefined) meta.clientEmail = String(fields.clientEmail);
    // Crew notes are a serialized Quill delta — same shape as job.crewSummary.
    if (fields.notes !== undefined) meta.dayNotes = String(fields.notes);
    // Optional job association (set/cleared via the dialog's omnisearch).
    if (fields.jobId !== undefined) meta.jobId = fields.jobId ? String(fields.jobId) : '';
    if (fields.jobCode !== undefined) meta.jobCode = fields.jobCode ? String(fields.jobCode) : '';
    return meta;
}

/** Plain text of a serialized Quill delta (or raw string fallback). */
export function opsTaskNotesPlainText(notes: string): string {
    try {
        const delta = JSON.parse(notes);
        return (delta?.ops || [])
            .filter((op: any) => typeof op.insert === 'string')
            .map((op: any) => op.insert)
            .join('')
            .trim();
    } catch {
        return notes;
    }
}

export type DispatchCallState = (typeof DISPATCH_CALL_STATES)[number];


export const BOOK_OFF_EVENT_TYPE = 'book-off';
export const NON_BILLABLE_EVENT_TYPE = 'non-billable';
export const RESERVE_EVENT_TYPE = 'reserve';
export const OPS_MANAGER_EVENT_TYPE = 'ops-manager';
export const OPS_TASK_EVENT_TYPE = 'ops-task';
export const CREW_LEAD_ROLE = 'crew-lead';
export const CREW_MEMBER_ROLE = 'crew-member';
export const DRIVER_ROLE = 'driver';
export const OPERATIONS_MANAGER_ROLE = 'ops-manager';

export enum AdditionalEventAttendeeRoles {
    All = 'All',
}

export type CrewRoleOptions = AdditionalEventAttendeeRoles | EventAttendeeRoles;

export enum DispatchUserStatusOptions {
    Available = 'Available',
    Assigned = 'Assigned',
    Unassigned = 'Unassigned',
    Unavailable = 'Unavailable',
    Active = 'Active',
    Inactive = 'Inactive',
    All = 'All'
}

export enum CrewSortOptions {
    Availability = 'Availability',
    LastName = 'Last Name',
    FirstName = 'First Name',
    RateHourly = 'Rate Hourly',
    EffectiveRate = 'Effective Rate',
}

export enum DispatchSortOptions {
    EventStartAsc = 'Event Start Ascending',
    EventStartDesc = 'Event Start Descending',
    TruckNameAsc = 'Truck Name Ascending',
    TruckNameDesc = 'Truck Name Descending',
}

export enum DispatchGroupOptions {
    None = 'None',
    Route = 'Route',
    StartTime = 'Start Time',
    EventType = 'Event Type',
    Status = 'Status',
    RequiresAttention = 'Requires Attention',
    PaidStatus = 'Paid Status',
}

export interface PotentialCrewMember {
    name: string;
    user: User;
    role: string;
    isDriver: boolean;
    eventsCount: number;
    revenue: number;
    isUnavailable: boolean;
    isBookedOff: boolean;
    isAssigned: boolean;
    estWorkingTime?: string;
    hoursWorked?: number;
    overtimeHours?: number;
}

export enum AdditionalEventStatuses {

    All = 'all',
}
export enum EventStatuses {
    Cancelled = 'cancelled',
    Required = 'required',
    Pending = 'pending',
    Booked = 'booked',
    Confirmed = 'confirmed',
    Completed = 'completed',
}

// This array contains a list of event statuses that the dispatch page should display
export enum DispatchAllowedToDisplayStatuses {
    Booked = EventStatuses.Booked,
    Confirmed = EventStatuses.Confirmed,
    Completed = EventStatuses.Completed
};

export type DispatchEventStatusOptions = AdditionalEventStatuses | DispatchAllowedToDisplayStatuses;


export const EVENT_STATUS_ICONS = {
    [AdditionalEventStatuses.All]: PrimeIcons.CHECK,
    [EventStatuses.Cancelled]: PrimeIcons.TIMES_CIRCLE,
    [EventStatuses.Required]: PrimeIcons.EXCLAMATION_TRIANGLE,
    [EventStatuses.Pending]: PrimeIcons.MINUS_CIRCLE,
    [EventStatuses.Booked]: PrimeIcons.CALENDAR,
    [EventStatuses.Confirmed]: PrimeIcons.THUMBS_UP,
    [EventStatuses.Completed]: PrimeIcons.CHECK_CIRCLE,
} as const;

// Define the Edit type
export interface UpdateCrewActionPayload {
    eventId: string;
    eventName: string;
    addAttendees?: Omit<AttendeeWithName, 'eventsCount' | 'hasConflict'>[];
    removeAttendees?: RemoveAttendees[];
    addAssets?: { id: string; name: string }[];
    removeAssets?: { id: string; name: string }[];
}
export interface JobUserWithName extends JobUser {
    name: string;
}

export type RemoveAttendees = Omit<AttendeeWithName, 'eventsCount' | 'hasConflict' | 'name'>;

export interface AttendeeWithName extends Attendee {
    name: string;
    eventsCount: number;
    hasConflict: boolean;
    conflictingEventIds?: string[];
    totalCost?: number;
    isOvertime?: boolean;
    hoursWorkedThisWeek?: number;
    overtimeHours?: number;
    tooltip?: string;
}

export interface TrucksWithMetadata extends Asset {
    eventsCount: number;
    hasConflict: boolean;
}


export interface DispatchFilterOption<T> {
    label: T;
    icon: PrimeIcons;
    value?: string;
}

export interface DispatchEvent {
    event: CalendarEventForDispatchFragment;
    style: {
        backgroundColor: string;
        color: string;
    };

    startTime: string;
    endTime: string;
    eventHasConflict: boolean;

    trucks: TrucksWithMetadata[];

    customer: JobUserWithName;

    crew: AttendeeWithName[];
    crewRequirement: number;
    needsMoreCrew: boolean;

    laborCharge: {
        name: string;
        color: string
    };

    totalLaborCost: number;
    laborPercentage: number;
}
/**
 * #7283 D (CodeRabbit round 2): did the dispatch day actually change?
 * loadEvents() dispatches hardRefreshEvents on every page load and the
 * live-update handlers do too — a draft-wipe must fire only for real
 * day switches, not same-day reloads/refreshes.
 */
function hasDayChanged(next: Date | string | undefined | null, current: Date | string | undefined | null): boolean {
    if (!next || !current) { return false; }
    return dayjs(next).format('YYYY-MM-DD') !== dayjs(current).format('YYYY-MM-DD');
}

export interface DispatchState {
    events: CalendarEventForDispatchFragment[];
    users: User[];
    roles: Role[];
    assets: Asset[];

    // Event Filter options
    dispatchDate: Date;
    // Map view range end (inclusive); null = single open day (dispatch).
    dispatchDateTo: Date | null;
    groupBy: DispatchGroupOptions;
    eventStatus: DispatchEventStatusOptions;

    // User filter options
    userSearch: string;
    userRole: CrewRoleOptions;
    userStatus: DispatchUserStatusOptions;
    userSort: CrewSortOptions;
    filteredCrewIds: string[] | null;

    isEditRateDialogOpen: boolean;
    isCreatingUser: boolean;
    selectedUserForRateEdit: User;
    selectedCrewSettingsTab: number;
    selectedUserBookOffEvents: CalendarEventForScheduleFragment[];

    // Send crew schedule (legacy)
    crewScheduleRecepients: PotentialCrewMember[];

    // Send Schedule to Crew dialog state
    isSendScheduleDialogOpen: boolean;
    /** Map of assetId -> { userId -> selected } */
    sendScheduleSelectedCrew: Record<string, Record<string, boolean>>;
    /** Map of assetId -> custom note for route */
    sendScheduleAssetNotes: Record<string, string>;
    /** Map of userId -> custom note for crew member */
    sendScheduleCrewNotes: Record<string, string>;
    /**
     * #7283 D (CodeRabbit ead65a67): send-batch bookkeeping — draft cleanup
     * must wait until EVERY route of one send() click reports back, because
     * an in-flight later route for the same user still needs the draft.
     */
    sendScheduleBatches: Record<string, { pending: number; hadError: boolean; sentUserIds: string[] }>;
    /** Map of `${assetId}:${userId}` -> preview data */
    sendSchedulePreviews: Record<string, { emailHtml: string; emailSubject: string; smsBody: string; loading: boolean }>;
    /** Map of assetId -> thread list */
    sendScheduleAssetThreads: Record<string, any[]>;
    /** Map of `${assetId}:${userId}` -> send status */
    sendScheduleCrewStatuses: Record<string, CrewScheduleSendStatus>;

    workerHistory: Record<string, CalendarEventForDispatchFragment[]>;

    // Map View
    hiddenMapRoutes: string[];
    dockLocation: any;

    // Pending changes queue (save/discard model)
    pendingChanges: DispatchPendingChange[];
    isSavingChanges: boolean;
    saveChangesError: Error | null;
    autosaveEnabled: boolean;
    /** Unix ms of the last successful events fetch — shown in the refresh tooltip. */
    lastEventsLoadedAt: number;
    /**
     * Unix ms when the LAST pending change was queued. Drives the
     * "saving in Ns" countdown in the header pill (autosave debounce is 5s).
     */
    lastPendingChangeAt: number | null;

    // Overtime tiers from zone config (dispatch.overtimeTiers)
    overtimeTiers: { thresholdHours: number; multiplier: number }[];

    // Default blocks from zone config (calendarEvents.defaultBlocks).
    // Parsed entries + which dates already had their defaults ensured so the
    // auto-create effect never double-creates (e.g. after a hard refresh).
    defaultBlocksConfig: DefaultBlockEntry[];
    defaultBlocksConfigLoading: boolean;
    /** ISO date strings (YYYY-MM-DD, local) that have had defaults ensured. */
    defaultBlocksEnsuredDates: string[];

    /**
     * Why the last "Assign Crew" run could not produce a schedule. Shown in the
     * assign-crew dialog until the dispatcher dismisses it; null when the last
     * run succeeded or nothing has run yet.
     */
    assignCrewInfeasibilityReasons: string[] | null;

    callState: {
        [name in DispatchCallState]: CallState;
    };
}

/** A single pending dispatch change (crew/asset add/remove/move) */
export interface DispatchPendingChange {
    id: string;
    type: 'addCrew' | 'removeCrew' | 'moveCrew' | 'addAsset' | 'removeAsset' | 'createEvent' | 'editEvent' | 'deleteEvent';
    eventId: string;
    eventName: string;
    userId?: string;
    userName?: string;
    role?: string;
    assetId?: string;
    assetName?: string;
    /** Date string (YYYY-MM-DD) — changes persist across days */
    date?: string;
    /** For moveCrew — the events to remove from */
    fromEvents?: { eventId: string; eventName: string; role?: string }[];
    /** For moveCrew — the events to add to */
    toEvents?: { eventId: string; eventName: string }[];
    /** For addCrew — whether the crew member is locked */
    locked?: boolean;
    /** For createEvent type — the synthetic event data */
    eventData?: any;
    /** For editEvent — new title */
    newTitle?: string;
    /** For editEvent — new start time (unix) */
    newStart?: number;
    /** For editEvent — new end time (unix) */
    newEnd?: number;
}

export const dispatchInitialState: DispatchState = {
    events: [],
    users: [],
    roles: [],
    assets: [],

    dispatchDate: new Date(), //TODO: Optimize: Store DayJs object instead of Date
    dispatchDateTo: null,
    groupBy: DispatchGroupOptions.Route,
    eventStatus: AdditionalEventStatuses.All,

    userSearch: '',
    userRole: AdditionalEventAttendeeRoles.All,
    userStatus: DispatchUserStatusOptions.Available,
    userSort: CrewSortOptions.Availability,
    filteredCrewIds: null,

    isEditRateDialogOpen: false,
    isCreatingUser: false,
    selectedUserForRateEdit: null,
    selectedCrewSettingsTab: 0,
    selectedUserBookOffEvents: [],
    crewScheduleRecepients: [],

    isSendScheduleDialogOpen: false,
    assignCrewInfeasibilityReasons: null,
    sendScheduleSelectedCrew: {},
    sendScheduleAssetNotes: {},
    sendScheduleCrewNotes: {},
    sendScheduleBatches: {},
    sendSchedulePreviews: {},
    sendScheduleAssetThreads: {},
    sendScheduleCrewStatuses: {},

    workerHistory: {},

    hiddenMapRoutes: [],
    dockLocation: null,

    pendingChanges: [],
    isSavingChanges: false,
    saveChangesError: null,
    autosaveEnabled: true,
    lastEventsLoadedAt: null,
    lastPendingChangeAt: null,
    overtimeTiers: [],
    defaultBlocksConfig: [],
    defaultBlocksConfigLoading: true,
    defaultBlocksEnsuredDates: [],

    callState: DISPATCH_CALL_STATES.reduce(
        (p, c) => ({ ...p, [c]: LoadingState.INIT }),
        {} as DispatchState['callState'],
    ),
};

/** Immutably updates one send-batch entry (no-op when unknown — e.g. effects replaying old actions). */
interface SendBatchEntry { pending: number; hadError: boolean; sentUserIds: string[]; }
function updateSendBatch(
    batches: DispatchState['sendScheduleBatches'],
    batchId: string,
    update: (b: SendBatchEntry) => SendBatchEntry,
): DispatchState['sendScheduleBatches'] {
    const existing = batches[batchId];
    if (!existing) { return { ...batches }; }
    return { ...batches, [batchId]: update(existing) };
}

export const DispatchFeature = createFeature({
    name: 'dispatch',

    reducer: createReducer(
        dispatchInitialState,

        // Load pending changes from localStorage — merge with existing (dedupe by ID)
        on(
            DispatchActions.loadPendingChangesFromStorage,
            (state, { changes }): DispatchState => {
                if (changes.length === 0) return state;
                const existingIds = new Set(state.pendingChanges.map((c) => c.id));
                // Two drop rules for restored createEvent changes:
                //
                // 1. A create whose event already exists on the server. This
                //    happens when a save committed server-side but the page
                //    unloaded before the success response — the restored queue
                //    would otherwise re-CREATE the event (the backend has no
                //    id-uniqueness constraint) and duplicate it.
                // 2. A create that matches a freshly queued one on
                //    type+name+start (this session's ensure may have already
                //    run — effect ordering — and queued its own creates).
                const serverEventIds = new Set(state.events.map((e) => e.id));
                const freshCreateKeys = new Set(
                    state.pendingChanges
                        .filter((c) => c.type === 'createEvent')
                        .map((c) => `${c.eventData?.eventType}:${c.eventName}:${c.eventData?.start}`),
                );
                const newChanges = changes.filter((c) => {
                    if (existingIds.has(c.id)) return false;
                    if (c.type === 'createEvent') {
                        if (serverEventIds.has(c.eventId)) return false;
                        if (freshCreateKeys.has(`${c.eventData?.eventType}:${c.eventName}:${c.eventData?.start}`)) {
                            return false;
                        }
                    }
                    return true;
                });
                return {
                    ...state,
                    pendingChanges: [...state.pendingChanges, ...newChanges],
                };
            },
        ),
        on(
            DispatchActions.eventsLoading,
            (state): DispatchState => ({
                ...state,
                callState: {
                    ...state.callState,
                    events: LoadingState.LOADING,
                },
            }),
        ),
            // ── Pending changes queue ──────────────────────────────────────────────
        on(
            DispatchActions.pendingChangeAdded,
            (state, { change }): DispatchState => {
                let events = state.events;
                // Deduplicate: replace existing ops-notes change for the same event
                let pendingChanges = [...state.pendingChanges];
                if (change.type === 'editEvent' && change.eventData?.opsNotes !== undefined) {
                    pendingChanges = pendingChanges.filter(
                        (c) => !(c.type === 'editEvent' && c.eventId === change.eventId
                            && c.eventData?.opsNotes !== undefined),
                    );
                }
                // Same for event notes (one pending change per event)
                if (change.type === 'editEvent' && change.eventData?.eventNotes !== undefined) {
                    pendingChanges = pendingChanges.filter(
                        (c) => !(c.type === 'editEvent' && c.eventId === change.eventId
                            && c.eventData?.eventNotes !== undefined),
                    );
                }
                // Same for day notes (ops-manager / ops-task card editors)
                if (change.type === 'editEvent' && change.eventData?.dayNotes !== undefined) {
                    pendingChanges = pendingChanges.filter(
                        (c) => !(c.type === 'editEvent' && c.eventId === change.eventId
                            && c.eventData?.dayNotes !== undefined),
                    );
                }
                pendingChanges = [...pendingChanges, change];

                // For editEvent changes, update the event optimistically
                if (change.type === 'editEvent') {
                    events = state.events.map((e) => {
                        if (e.id !== change.eventId) return e;
                        const updated: any = {
                            ...e,
                            title: change.newTitle ?? e.title,
                            start: change.newStart ?? e.start,
                            end: change.newEnd ?? e.end,
                        };
                        // Update ops notes in job metadata optimistically
                        if (change.eventData?.opsNotes !== undefined && e.job) {
                            updated.job = {
                                ...e.job,
                                metadata: {
                                    ...(e.job.metadata as any || {}),
                                    calculatorOutput__opsNotes: change.eventData.opsNotes,
                                },
                            };
                        }
                        // Event notes ride in EVENT metadata as the serialized
                        // Quill delta (same store the workorders page's Event
                        // Notes section writes) — merge optimistically so a
                        // refresh landing mid-typing doesn't blank the editor
                        // while the change is still queued.
                        if (change.eventData?.eventNotes !== undefined) {
                            updated.metadata = {
                                ...(e.metadata as any || {}),
                                eventNotes: change.eventData.eventNotes,
                            };
                        }
                        // Update crew requirement in event metadata optimistically
                        if (change.eventData?.crewRequirement) {
                            updated.metadata = {
                                ...(e.metadata as any || {}),
                                crewRequirement: JSON.stringify(change.eventData.crewRequirement),
                            };
                        }
                        // Day notes (ops-manager / ops-task card editors) ride in
                        // event metadata as the serialized Quill delta — merge
                        // optimistically so a refresh landing mid-typing doesn't
                        // blank the editor while the change is still queued.
                        if (change.eventData?.dayNotes !== undefined) {
                            updated.metadata = {
                                ...(e.metadata as any || {}),
                                dayNotes: change.eventData.dayNotes,
                            };
                        }
                        // Update OPS task fields in event metadata optimistically
                        // (mirrors the shape SavePendingChanges writes: one
                        // metadata_<key> per task field)
                        if (change.eventData?.opsTaskFields) {
                            updated.metadata = {
                                ...(e.metadata as any || {}),
                                ...opsTaskFieldsToMetadata(change.eventData.opsTaskFields),
                            };
                            if (change.eventData.opsTaskFields.title) {
                                updated.title = change.eventData.opsTaskFields.title;
                            }
                            // Card fallback text: plain text derived from the notes delta.
                            if (change.eventData.opsTaskFields.notes !== undefined) {
                                updated.description = opsTaskNotesPlainText(change.eventData.opsTaskFields.notes);
                            }
                        }
                        // Completed toggle: optimistic status update
                        if (change.eventData?.opsTaskStatus !== undefined) {
                            updated.status = change.eventData.opsTaskStatus;
                        }
                        return updated;
                    });
                    // If there's a createEvent for this event, update its eventData too
                    pendingChanges = pendingChanges.map((c) => {
                        if (c.type === 'createEvent' && c.eventId === change.eventId && c.eventData) {
                            return {
                                ...c,
                                eventData: {
                                    ...c.eventData,
                                    start: change.newStart ?? c.eventData.start,
                                    end: change.newEnd ?? c.eventData.end,
                                    description: change.newTitle ?? c.eventData.description,
                                },
                                eventName: change.newTitle ?? c.eventName,
                            };
                        }
                        return c;
                    });
                    return { ...state, events, pendingChanges, lastPendingChangeAt: Date.now() };
                }

                // For createEvent changes, add the synthetic event to state
                // (unless it was already added by the reserveEventCreated etc. reducer)
                if (change.type === 'createEvent' && change.eventData) {
                    const ed = change.eventData;
                    const alreadyExists = events.some((e) => e.id === change.eventId);
                    if (!alreadyExists) {
                        const type = ed.eventType
                            || NON_BILLABLE_EVENT_TYPE;
                        events = [...events, synthesizePlaceholderEvent(
                            { ...ed, description: ed.description },
                            type,
                            change.eventName,
                        ) as any];
                    }
                }

                // For addCrew/removeCrew/moveCrew on a synthetic event (one with a
                // createEvent pending change), update the createEvent's attendees
                // instead of creating a separate bulk edit change that would fail
                // (the event doesn't exist on the server yet).
                if (change.type === 'addCrew' || change.type === 'removeCrew') {
                    const createChange = pendingChanges.find(
                        (c) => c.type === 'createEvent' && c.eventId === change.eventId,
                    );
                    if (createChange) {
                        // Merge the crew change into the createEvent's eventData
                        if (change.type === 'addCrew' && createChange.eventData) {
                            if (!createChange.eventData.userId && change.userId) {
                                createChange.eventData.userId = change.userId;
                            }
                            if (!createChange.eventData.attendees) {
                                createChange.eventData.attendees = [];
                            }
                            createChange.eventData.attendees.push({
                                userId: change.userId,
                                role: change.role || EventAttendeeRoles.crewMember,
                            });
                        }
                        if (change.type === 'removeCrew' && createChange.eventData) {
                            if (createChange.eventData.attendees) {
                                createChange.eventData.attendees = createChange.eventData.attendees
                                    .filter((a: any) => a.userId !== change.userId);
                            }
                            if (createChange.eventData.userId === change.userId) {
                                createChange.eventData.userId = undefined;
                            }
                        }
                        // Don't add the separate addCrew/removeCrew change —
                        // it's merged into the createEvent
                        pendingChanges = pendingChanges.filter((c) => c.id !== change.id);

                        // Also update the synthetic event in state
                        events = state.events.map((e) => {
                            if (e.id !== change.eventId) return e;
                            if (change.type === 'addCrew') {
                                return {
                                    ...e,
                                    attendees: [...e.attendees, {
                                        role: change.role || EventAttendeeRoles.crewMember,
                                        user: { id: change.userId } as any,
                                    }],
                                };
                            } else {
                                return {
                                    ...e,
                                    attendees: e.attendees.filter((a) => a.user?.id !== change.userId),
                                };
                            }
                        });
                    }
                }

                // For addCrew/removeCrew/moveCrew on existing server events,
                // apply optimistic update to the attendees array.
                if ((change.type === 'addCrew' || change.type === 'removeCrew' || change.type === 'moveCrew')
                        && !pendingChanges.some((c) => c.type === 'createEvent' && c.eventId === change.eventId)) {

                    if (change.type === 'removeCrew' && change.fromEvents?.length) {
                        events = state.events.map((e) => {
                            if (!change.fromEvents!.some((f) => f.eventId === e.id)) return e;
                            return {
                                ...e,
                                attendees: e.attendees.filter((a) => a.user?.id !== change.userId),
                            };
                        });
                    } else if (change.type === 'removeCrew') {
                        events = state.events.map((e) => {
                            if (e.id !== change.eventId) return e;
                            return {
                                ...e,
                                attendees: e.attendees.filter((a) => a.user?.id !== change.userId),
                            };
                        });
                    } else if (change.type === 'addCrew') {
                        events = state.events.map((e) => {
                            if (e.id !== change.eventId) return e;
                            if (e.attendees.some((a) => a.user?.id === change.userId)) return e;
                            return {
                                ...e,
                                attendees: [...e.attendees, {
                                    role: change.role || EventAttendeeRoles.crewMember,
                                    user: { id: change.userId } as any,
                                    locked: false,
                                } as any],
                            };
                        });
                    }
                }

                return {
                    ...state,
                    events,
                    pendingChanges,
                    lastPendingChangeAt: Date.now(),
                };
            },
        ),
        on(
            DispatchActions.pendingChangeAndOptimisticUpdate,
            (state, { change, edits }): DispatchState => {
                // Add to pending queue + apply optimistic update
                const editMap = new Map(edits.map((e) => [e.eventId, e]));
                const events = state.events.map((event) => {
                    const edit = editMap.get(event.id);
                    if (!edit) return event;

                    const removeAttendeeIds = new Set(
                        (edit.removeAttendees || []).map((a) => a.user.id),
                    );
                    const newAttendees = event.attendees
                        .filter((a) => !removeAttendeeIds.has(a.user.id))
                        .concat(
                            (edit.addAttendees || []).map((add) => ({
                                role: add.role,
                                user: add.user,
                                locked: (add as any).locked || false,
                            })),
                        );

                    return { ...event, attendees: newAttendees };
                });

                return {
                    ...state,
                    events,
                    pendingChanges: [...state.pendingChanges, change],
                    lastPendingChangeAt: Date.now(),
                };
            },
        ),
        on(
            DispatchActions.pendingChangeRemoved,
            (state, { changeId }): DispatchState => ({
                ...state,
                pendingChanges: state.pendingChanges.filter((c) => c.id !== changeId),
            }),
        ),
        on(
            DispatchActions.saveChangesClicked,
            (state): DispatchState => ({
                ...state,
                isSavingChanges: true,
            }),
        ),
        on(
            DispatchActions.saveChangesSuccess,
            (state, { changeIds }): DispatchState => ({
                ...state,
                isSavingChanges: false,
                saveChangesError: null,
                pendingChanges: state.pendingChanges.filter((c) => !changeIds.includes(c.id)),
                // Queue drained (or shrank) — reset the countdown clock
                lastPendingChangeAt: state.pendingChanges.filter((c) => !changeIds.includes(c.id)).length > 0
                    ? state.lastPendingChangeAt
                    : null,
            }),
        ),
        on(
            DispatchActions.saveChangesPartialSuccess,
            (state, { succeededChangeIds, failedChangeIds }): DispatchState => ({
                ...state,
                isSavingChanges: false,
                saveChangesError: null,
                // Requirement: Disable autosave on partial failure so failing
                // changes are not resubmitted on every timer cycle. The user
                // can re-enable autosave after fixing the issue.
                autosaveEnabled: failedChangeIds.length > 0 ? false : state.autosaveEnabled,
                // Remove only succeeded changes; failed ones stay queued
                pendingChanges: state.pendingChanges.filter((c) => !succeededChangeIds.includes(c.id)),
            }),
        ),
        on(
            DispatchActions.saveChangesError,
            (state, { error }): DispatchState => ({
                ...state,
                isSavingChanges: false,
                saveChangesError: error,
                autosaveEnabled: false,
            }),
        ),
        on(
            DispatchActions.setAutosaveEnabled,
            (state, { enabled }): DispatchState => ({
                ...state,
                autosaveEnabled: enabled,
                ...(enabled ? { saveChangesError: null } : {}),
            }),
        ),
        on(
            DispatchActions.setOvertimeTiers,
            (state, { tiers }): DispatchState => ({
                ...state,
                overtimeTiers: tiers,
            }),
        ),
        // ── Default blocks (calendarEvents.defaultBlocks) ────────────────────
        on(
            DispatchActions.defaultBlocksConfigLoading,
            (state): DispatchState => ({
                ...state,
                defaultBlocksConfigLoading: true,
            }),
        ),
        on(
            DispatchActions.defaultBlocksConfigLoaded,
            (state, { entries }): DispatchState => ({
                ...state,
                defaultBlocksConfig: entries,
                defaultBlocksConfigLoading: false,
                // The config can change mid-session ("Set/Remove as Default"
                // in the edit-block dialog saves straight through here), so
                // the per-day "already ensured" bookkeeping is stale — clear
                // it or already-visited days never backfill/re-clean against
                // the fresh entries (issue #6983).
                defaultBlocksEnsuredDates: [],
            }),
        ),
        on(
            DispatchActions.defaultBlocksConfigLoadError,
            (state): DispatchState => ({
                ...state,
                defaultBlocksConfigLoading: false,
            }),
        ),
        // Zone switch / manual refresh: the default-blocks config is reloaded
        // per zone, so per-date "already ensured" bookkeeping must reset —
        // otherwise the new zone never gets its own defaults this session.
        on(
            DispatchActions.componentHardRefreshed,
            (state): DispatchState => ({
                ...state,
                defaultBlocksEnsuredDates: [],
            }),
        ),
        on(
            DispatchActions.missingDefaultBlocksEnsured,
            (state, { date, inputs }): DispatchState => {
                // Record the date as ensured even when nothing was missing, so
                // repeated hard refreshes of the same day don't re-run matching.
                // Race-proof: when two eventsLoaded actions arrive in the same
                // tick (Apollo batching), both ensure runs may pass the effect's
                // store checks before either commits — filter here against
                // already-queued creates of the same type+name for this date.
                const seenKeys = new Set(
                    state.pendingChanges
                        .filter((c) => c.type === 'createEvent')
                        .map((c) => `${date}:${c.eventData?.eventType}:${c.eventName}`),
                );
                const newInputs = inputs.filter((input) =>
                    !seenKeys.has(`${date}:${input.blockType}:${input.name}`),
                );

                const events = [...state.events];
                for (const input of newInputs) {
                    events.push(synthesizePlaceholderEvent(
                        {
                            eventId: input.eventId,
                            start: input.start,
                            end: input.end,
                            description: input.name,
                            metadata: { [AUTO_GENERATED_META_KEY]: AUTO_GENERATED_META_VALUE },
                        },
                        input.blockType,
                        input.name,
                    ) as any);
                }
                return {
                    ...state,
                    events,
                    defaultBlocksEnsuredDates: state.defaultBlocksEnsuredDates.includes(date)
                        ? state.defaultBlocksEnsuredDates
                        : [...state.defaultBlocksEnsuredDates, date],
                };
            },
        ),
        on(
            DispatchActions.defaultBlocksResetConfirmed,
            (state, { deleteEventIds, inputs }): DispatchState => {
                // Optimistic events only — the queueing side (deletes + creates)
                // is handled by effects dispatching blockDeleted/pendingChangeAdded
                // so autosave and localStorage persistence see every change.
                let events = state.events.filter((e) => !deleteEventIds.includes(e.id));
                for (const input of inputs) {
                    events = [...events, synthesizePlaceholderEvent(
                        {
                            eventId: input.eventId,
                            start: input.start,
                            end: input.end,
                            description: input.name,
                            metadata: { [AUTO_GENERATED_META_KEY]: AUTO_GENERATED_META_VALUE },
                        },
                        input.blockType,
                        input.name,
                    ) as any];
                }

                return {
                    ...state,
                    events,
                };
            },
        ),
        on(
            DispatchActions.changesDiscarded,
            (state): DispatchState => ({
                ...state,
                pendingChanges: [],
                // The day's defaults must be re-ensurable, or the board
                // silently loses its default blocks until a hard refresh.
                defaultBlocksEnsuredDates: [],
            }),
        ),
        on(
            DispatchActions.blockDeleted,
            (state, { eventId }): DispatchState => ({
                ...state,
                events: state.events.filter((e) => e.id !== eventId),
                pendingChanges: [
                    ...state.pendingChanges.filter((c) => c.eventId !== eventId || c.type === 'deleteEvent'),
                    ...(!state.pendingChanges.some((c) => c.eventId === eventId && c.type === 'deleteEvent')
                        ? [{ id: `delete-${eventId}-${Date.now()}`, type: 'deleteEvent' as const, eventId, eventName: '' } as any]
                        : []),
                ],
                lastPendingChangeAt: Date.now(),
            }),
        ),
        // Clear pending changes when events are reloaded from server
        // (changes should have been saved or discarded before refresh)
        on(
            DispatchActions.eventsLoaded,
            (state, { events }): DispatchState => {
                // TODO: We should do this filtering in the backend
                // Keep book-off events out of the main list, but show
                // non-billable and reserve events so dispatchers can track them.
                const filteredEventsWithCrewOnly = events
                    .filter((event) => event.type !== BOOK_OFF_EVENT_TYPE)
                    .map((event) => ({
                        ...event,
                        attendees: event.attendees.filter(attendee =>
                            eventAttendeeRolesArray.includes(attendee.role as any)
                        ),
                    }));

                // A refresh (date change, hard refresh, save-success reload)
                // replaces the event list wholesale — pending createEvent
                // changes' optimistic placeholders would vanish from the board
                // while still queued for save. Re-synthesize any placeholder
                // the loaded set doesn't already contain (matched by id), but
                // only ones that belong to the day being loaded — pending
                // creates for other days persist in the queue without being
                // plastered onto this day's board.
                const loadedIds = new Set(filteredEventsWithCrewOnly.map((e) => e.id));
                const dayStart = dayjs(state.dispatchDate).startOf('day').unix();
                const dayEnd = dayjs(state.dispatchDate).endOf('day').unix();
                const recreatedPlaceholders: CalendarEventForScheduleFragment[] = [];
                for (const change of state.pendingChanges) {
                    if (change.type !== 'createEvent' || loadedIds.has(change.eventId)) continue;
                    const ed = change.eventData || {};
                    const start = ed.start ?? change.newStart;
                    const end = ed.end ?? change.newEnd;
                    // Blocks that start on the current day, or overnight blocks
                    // started the previous day whose end reaches into it.
                    // Anything else belongs to a different day and must not
                    // be plastered onto this day's board.
                    const startsThisDay = typeof start === 'number'
                        && start >= dayStart && start <= dayEnd;
                    const spansIntoDay = typeof start === 'number' && typeof end === 'number'
                        && start < dayStart && end > dayStart;
                    if (!startsThisDay && !spansIntoDay) continue;
                    recreatedPlaceholders.push(synthesizePlaceholderEvent(
                        {
                            eventId: change.eventId,
                            userId: ed.userId,
                            start: ed.start,
                            end: ed.end,
                            description: ed.description || change.eventName,
                            metadata: ed.metadata,
                        },
                        ed.eventType || NON_BILLABLE_EVENT_TYPE,
                        change.eventName,
                    ) as any);
                }

                return {
                    ...state,
                    events: [...filteredEventsWithCrewOnly, ...recreatedPlaceholders],
                    lastEventsLoadedAt: Date.now(),
                    callState: {
                        ...state.callState,
                        events: LoadingState.LOADED,
                    },
                };
            },
        ),

        /**
         * After a refresh/save, merge pending event notes back onto the
         * freshly loaded server events. The notes live on the EVENT's
         * metadata.eventNotes, so a wholesale eventsLoaded would otherwise
         * revert the editor to the server's last-saved value while the
         * change is still queued.
         */
        on(
            DispatchActions.replayPendingCrewNotes,
            (state, { changes, events }): DispatchState => {
                const crewNotesByEvent = new Map<string, string>();
                for (const change of changes) {
                    if (change.type === 'editEvent' && change.eventData?.eventNotes !== undefined) {
                        crewNotesByEvent.set(change.eventId, change.eventData.eventNotes);
                    }
                }

                // Day notes (ops-manager / ops-task cards) live on EVENT
                // metadata — a refresh landing mid-edit would otherwise show
                // the server's (possibly empty) value while the change is
                // still queued.
                const dayNotesByEvent = new Map<string, string>();
                for (const change of changes) {
                    if (change.type === 'editEvent' && change.eventData?.dayNotes !== undefined) {
                        dayNotesByEvent.set(change.eventId, change.eventData.dayNotes);
                    }
                }

                // getEvents passes the freshly loaded events; the replay effect
                // (loadPendingChangesFromStorage / saveChangesSuccess) passes []
                // to mean "re-apply onto the events already in the store".
                const baseEvents = events.length > 0 ? events : state.events;

                const filteredEventsWithCrewOnly = baseEvents
                    .filter((event) => event.type !== BOOK_OFF_EVENT_TYPE)
                    .map((event) => ({
                        ...event,
                        attendees: event.attendees.filter(attendee =>
                            eventAttendeeRolesArray.includes(attendee.role as any)
                        ),
                    }));

                const loadedIds = new Set(filteredEventsWithCrewOnly.map((e) => e.id));
                const dayStart = dayjs(state.dispatchDate).startOf('day').unix();
                const dayEnd = dayjs(state.dispatchDate).endOf('day').unix();
                const recreatedPlaceholders: CalendarEventForScheduleFragment[] = [];
                for (const change of state.pendingChanges) {
                    if (change.type !== 'createEvent' || loadedIds.has(change.eventId)) continue;
                    const ed = change.eventData || {};
                    const start = ed.start ?? change.newStart;
                    const end = ed.end ?? change.newEnd;
                    const startsThisDay = typeof start === 'number'
                        && start >= dayStart && start <= dayEnd;
                    const spansIntoDay = typeof start === 'number' && typeof end === 'number'
                        && start < dayStart && end > dayStart;
                    if (!startsThisDay && !spansIntoDay) continue;
                    recreatedPlaceholders.push(synthesizePlaceholderEvent(
                        {
                            eventId: change.eventId,
                            userId: ed.userId,
                            start: ed.start,
                            end: ed.end,
                            description: ed.description || change.eventName,
                            metadata: ed.metadata,
                        },
                        ed.eventType || NON_BILLABLE_EVENT_TYPE,
                        change.eventName,
                    ) as any);
                }

                const mergedEvents = [...filteredEventsWithCrewOnly, ...recreatedPlaceholders].map((e) => {
                    const notes = crewNotesByEvent.get(e.id);
                    const pendingDayNotes = dayNotesByEvent.get(e.id);
                    if (notes === undefined && pendingDayNotes === undefined) return e;
                    let nextEvent: any = { ...e };

                    // Day notes: merge the queued delta into the event metadata
                    if (pendingDayNotes !== undefined) {
                        nextEvent = {
                            ...nextEvent,
                            metadata: {
                                ...(e.metadata as any || {}),
                                dayNotes: pendingDayNotes,
                            },
                        };
                    }

                    // Event notes: merge the queued delta into the event metadata
                    if (notes !== undefined) {
                        nextEvent = {
                            ...nextEvent,
                            metadata: {
                                ...(nextEvent.metadata as any || {}),
                                eventNotes: notes,
                            },
                        };
                    }

                    return nextEvent;
                });

                return {
                    ...state,
                    events: mergedEvents,
                    callState: {
                        ...state.callState,
                        events: LoadingState.LOADED,
                    },
                };
            },
        ),

        on(
            DispatchActions.setGroupBy,
            (state, { groupBy }): DispatchState => ({
                ...state,
                groupBy,
            }),
        ),
        on(
            DispatchActions.setDispatchDate,
            (state, { date }): DispatchState => ({
                ...state,
                dispatchDate: date,
                // #7283 D scoping (CodeRabbit): send-dialog drafts are keyed
                // by user only and must not bleed onto another day's events —
                // discard them ONLY when the day actually changed (a same-day
                // reload must keep the drafts alive; CodeRabbit round 2).
                sendScheduleCrewNotes: hasDayChanged(date, state.dispatchDate)
                    ? {} : state.sendScheduleCrewNotes,
            }),
        ),
        on(
            DispatchActions.hardRefreshEvents,
            (state, { date }): DispatchState => ({
                ...state,
                dispatchDate: date ?? state.dispatchDate,
                // Same day-compare as setDispatchDate: loadEvents() runs a
                // hard refresh on EVERY page load — clearing unconditionally
                // would erase drafts the moment the page reloads.
                sendScheduleCrewNotes: hasDayChanged(date, state.dispatchDate)
                    ? {} : state.sendScheduleCrewNotes,
            }),
        ),
        on(
            DispatchActions.setDispatchDateTo,
            (state, { date }): DispatchState => ({
                ...state,
                dispatchDateTo: date,
            }),
        ),
        on(
            DispatchActions.setEventStatus,
            (state, { eventStatus }): DispatchState => ({
                ...state,
                eventStatus,
            }),
        ),

        on(
            DispatchActions.setUserSearch,
            (state, { search }): DispatchState => ({
                ...state,
                userSearch: search,
            }),
        ),

        on(
            DispatchActions.setUserRole,
            (state, { role }): DispatchState => ({
                ...state,
                userRole: role,
            }),
        ),

        on(
            DispatchActions.setUserStatus,
            (state, { status }): DispatchState => ({
                ...state,
                userStatus: status,
            }),
        ),

        on(
            DispatchActions.setCrewSort,
            (state, { sort }): DispatchState => ({
                ...state,
                userSort: sort,
            }),
        ),

        on(
            DispatchActions.setFilteredCrewIds,
            (state, { ids }): DispatchState => ({
                ...state,
                filteredCrewIds: ids,
            }),
        ),

        on(
            DispatchActions.rolesLoaded,
            DispatchActions.rolesLoadedOnComponentHardRefresh,
            (state, { roles }): DispatchState => ({
                ...state,
                roles,
                callState: {
                    ...state.callState,
                    roles: LoadingState.LOADED,
                },
            }),
        ),

        on(
            DispatchActions.assetsLoading,
            (state): DispatchState => ({
                ...state,
                callState: {
                    ...state.callState,
                    assets: LoadingState.LOADING,
                },
            }),
        ),

        on(
            DispatchActions.assetsLoaded,
            (state, { assets }): DispatchState => ({
                ...state,
                assets,
                callState: {
                    ...state.callState,
                    assets: LoadingState.LOADED,
                },
            }),
        ),

        on(
            DispatchActions.usersLoading,
            (state): DispatchState => ({
                ...state,
                callState: {
                    ...state.callState,
                    users: LoadingState.LOADING,
                },
            }),
        ),

        on(
            DispatchActions.usersLoaded,
            (state, { users }): DispatchState => ({
                ...state,
                users,
                callState: {
                    ...state.callState,
                    users: LoadingState.LOADED,
                },
            }),
        ),

        on(DispatchActions.updateCrew, (state, { edits }): DispatchState => {
            const events = cloneDeep(state.events);
            // A map of eventId to edits for quick lookup
            const editMap = new Map(edits.map((edit) => [edit.eventId, edit]));

            const updatedEvents = events.map((event) => {
                const edit = editMap.get(event.id);
                if (!edit) {
                    return event;
                }

                const {
                    addAttendees = [],
                    removeAttendees = [],
                    addAssets = [],
                    removeAssets = [],
                } = edit;

                // Set of user ids to remove
                const removeAttendeeIds = new Set(
                    removeAttendees.map((attendee) => attendee.user.id),
                );
                const removeAssetIds = new Set(
                    removeAssets.map((asset) => asset.id),
                );

                const newAttendees = event.attendees
                    .filter(
                        (attendee) => !removeAttendeeIds.has(attendee.user.id),
                    )
                    .concat(
                        addAttendees.map((addAttendee) => ({
                            role: addAttendee.role,
                            user: addAttendee.user,
                            locked: addAttendee.locked,
                        })),
                    );

                const newAssets = event.assets
                    .filter((asset) => !removeAssetIds.has(asset.id))
                    .concat(
                        addAssets.map((addAsset) => ({
                            id: addAsset.id,
                            name: addAsset.name,
                            type: 'Truck',
                        })),
                    );

                return {
                    ...event,
                    attendees: newAttendees,
                    assets: newAssets,
                };
            });

            return {
                ...state,
                events: updatedEvents,
                callState: {
                    ...state.callState,
                    updateCrew: LoadingState.MUTATING,
                },
            };
        }),

        on(
            DispatchActions.updateCrewSuccess,
            (state): DispatchState => ({
                ...state,
                callState: {
                    ...state.callState,
                    updateCrew: LoadingState.MUTATED,
                },
            }),
        ),

        on(
            DispatchActions.updateCrewError,
            (state, { edits, error }): DispatchState => {
                // Revert the changes
                // 1. Remove attendees that were added (from addAttendees)
                // 2. Add back attendees that were removed (from removeAttendees)

                const events = cloneDeep(state.events);
                // A map of eventId to edits for quick lookup
                const editMap = new Map(
                    edits.map((edit) => [edit.eventId, edit]),
                );

                const updatedEvents = events.map((event) => {
                    const edit = editMap.get(event.id);

                    if (!edit) {
                        return event;
                    }

                    const { addAttendees = [], removeAttendees = [], addAssets = [], removeAssets = [] } = edit;

                    const addAttendeeIds = new Set(
                        addAttendees.map((attendee) => attendee.user.id),
                    );

                    const addAssetIds = new Set(
                        addAssets.map((addAsset) => addAsset.id),
                    );

                    const newAttendees = event.attendees
                        .filter(
                            (attendee) => !addAttendeeIds.has(attendee.user.id),
                        )
                        .concat(
                            removeAttendees.map((removedAttendee) => ({
                                user: removedAttendee.user,
                                role: removedAttendee.role,
                            })),
                        );

                    const newAssets = event.assets
                        .filter((asset) => !addAssetIds.has(asset.id))
                        .concat(
                            removeAssets.map((removedAsset) => ({
                                id: removedAsset.id,
                                name: removedAsset.name,
                                type: 'Truck',
                            })),
                        );

                    return {
                        ...event,
                        attendees: newAttendees,
                        assets: newAssets,
                    };
                });

                return {
                    ...state,
                    events: updatedEvents,
                    callState: {
                        ...state.callState,
                        updateCrew: {
                            error,
                        },
                    },
                };
            },
        ),
        on(DispatchActions.recepientsForCrewScheduleChanged, (state, { recepients }): DispatchState => ({
            ...state,
            crewScheduleRecepients: recepients,
        })),
        on(DispatchActions.crewScheduleSendSuccess, (state): DispatchState => ({
            ...state,
            callState: {
                ...state.callState,
                crewScheduleSend: LoadingState.MUTATED,
            },
        })),

        // ─── Send Schedule to Crew dialog ───────────────────────────────────

        on(DispatchActions.sendScheduleDialogOpened, (state): DispatchState => ({
            ...state,
            isSendScheduleDialogOpen: true,
            // Preserve notes and selections across open/close so work isn't lost.
            // Only reset previews (they are ephemeral/stale after reopening).
            sendSchedulePreviews: {},
        })),

        on(DispatchActions.sendScheduleDialogClosed, (state): DispatchState => ({
            ...state,
            isSendScheduleDialogOpen: false,
        })),

        on(DispatchActions.sendScheduleCrewInitialized, (state, { initialSelections }): DispatchState => {
            // Seed every crew member into the selected map without overwriting existing selections.
            // This ensures sendScheduleAllChangedToggled can find them.
            const next = { ...state.sendScheduleSelectedCrew };
            for (const { assetId, userId, selected } of initialSelections) {
                if (!next[assetId]) { next[assetId] = {}; }
                // Only set the default if no explicit selection has been made yet
                if (!(userId in next[assetId])) {
                    next[assetId] = { ...next[assetId], [userId]: selected };
                }
            }
            return { ...state, sendScheduleSelectedCrew: next };
        }),

        on(DispatchActions.sendScheduleCrewToggled, (state, { userId, assetId, selected }): DispatchState => {
            const assetCrew = { ...(state.sendScheduleSelectedCrew[assetId] || {}) };
            assetCrew[userId] = selected;
            return {
                ...state,
                sendScheduleSelectedCrew: {
                    ...state.sendScheduleSelectedCrew,
                    [assetId]: assetCrew,
                },
            };
        }),

        // sendScheduleAllChangedToggled is handled by an effect that reads the
        // route tree selector and dispatches sendScheduleBulkSelectionApplied.
        // The reducer itself does nothing for this action.

        on(DispatchActions.sendScheduleBulkSelectionApplied, (state, { selections }): DispatchState => {
            const next = { ...state.sendScheduleSelectedCrew };
            for (const { assetId, userId, selected } of selections) {
                if (!next[assetId]) { next[assetId] = {}; }
                next[assetId] = { ...next[assetId], [userId]: selected };
            }
            return { ...state, sendScheduleSelectedCrew: next };
        }),

        // sendScheduleDeselectAll is handled by an effect (same pattern as allChangedToggled)
        // to ensure it reads the route tree and not just existing state map keys.

        on(DispatchActions.sendScheduleCustomNoteChanged, (state, { assetId, note }): DispatchState => ({
            ...state,
            sendScheduleAssetNotes: { ...state.sendScheduleAssetNotes, [assetId]: note },
        })),

        on(DispatchActions.sendScheduleCrewCustomNoteChanged, (state, { userId, note }): DispatchState => ({
            ...state,
            sendScheduleCrewNotes: { ...state.sendScheduleCrewNotes, [userId]: note },
        })),

        on(DispatchActions.sendScheduleCrewNotesRestored, (state, { notes }): DispatchState => {
            // Only truthy notes are restored — explicit clearing (empty string
            // in a live session) is never resurrected from storage.
            const restored: Record<string, string> = { ...(state.sendScheduleCrewNotes || {}) };
            for (const [userId, note] of Object.entries(notes || {})) {
                if (typeof note !== 'string' || !note.trim()) continue;
                // Only fill users with NO live entry (CodeRabbit): an
                // explicitly cleared note ('' live) must stay cleared even if
                // another tab later writes a stale value into storage.
                if (!(userId in restored)) { restored[userId] = note; }
            }
            return {
                ...state,
                sendScheduleCrewNotes: restored,
            };
        }),

        on(DispatchActions.sendSchedulePreviewRequested, (state, { assetId, userId }): DispatchState => {
            const key = `${assetId}:${userId}`;
            return {
                ...state,
                sendSchedulePreviews: {
                    ...state.sendSchedulePreviews,
                    [key]: { emailHtml: '', emailSubject: '', smsBody: '', loading: true },
                },
            };
        }),

        on(DispatchActions.sendSchedulePreviewLoaded, (state, { assetId, userId, emailHtml, emailSubject, smsBody }): DispatchState => {
            const key = `${assetId}:${userId}`;
            return {
                ...state,
                sendSchedulePreviews: {
                    ...state.sendSchedulePreviews,
                    [key]: { emailHtml, emailSubject, smsBody, loading: false },
                },
            };
        }),

        on(DispatchActions.sendSchedulePreviewError, (state, { assetId, userId }): DispatchState => {
            const key = `${assetId}:${userId}`;
            return {
                ...state,
                sendSchedulePreviews: {
                    ...state.sendSchedulePreviews,
                    [key]: { emailHtml: '', emailSubject: '', smsBody: '', loading: false },
                },
            };
        }),

        on(DispatchActions.sendScheduleAssetThreadsLoaded, (state, { assetId, threads }): DispatchState => ({
            ...state,
            sendScheduleAssetThreads: { ...state.sendScheduleAssetThreads, [assetId]: threads },
        })),

        on(DispatchActions.sendScheduleCrewStatusesLoaded, (state, { statuses }): DispatchState => {
            const newStatuses = { ...state.sendScheduleCrewStatuses };
            for (const { userId, assetId, status } of statuses) {
                newStatuses[`${assetId}:${userId}`] = status;
            }
            return { ...state, sendScheduleCrewStatuses: newStatuses };
        }),

        on(DispatchActions.sendScheduleSubmitted, (state): DispatchState => ({
            ...state,
            callState: { ...state.callState, sendScheduleSubmit: LoadingState.LOADING },
        })),

        on(DispatchActions.sendScheduleBatchStarted, (state, { batchId, routeCount }): DispatchState => ({
            ...state,
            sendScheduleBatches: {
                ...state.sendScheduleBatches,
                [batchId]: { pending: routeCount, hadError: false, sentUserIds: [] },
            },
        })),

        on(DispatchActions.sendScheduleSubmitSuccess, (state, { batchId, sentUserIds }): DispatchState => ({
            ...state,
            // Requirement: Do not close the dialog on the first per-route success —
            // a multi-route send dispatches one sendScheduleSubmitted per route and
            // each triggers its own sendScheduleSubmitSuccess. Closing early makes
            // partial success look complete and hides later failures. The dialog is
            // closed explicitly when the user triggers sendScheduleDialogClosed.
            callState: { ...state.callState, sendScheduleSubmit: LoadingState.MUTATED },
            // Batch bookkeeping only — drafts clear on BatchDraftCleanup when
            // the WHOLE batch succeeded (CodeRabbit ead65a67), not per route.
            sendScheduleBatches: updateSendBatch(state.sendScheduleBatches, batchId, (b) => ({
                ...b,
                pending: Math.max(0, b.pending - 1),
                sentUserIds: Array.from(new Set([...(b.sentUserIds || []), ...(sentUserIds || [])])),
            })),
        })),

        on(DispatchActions.sendScheduleCrewDraftsClearSent, (state, { batchId, sentUserIds }): DispatchState => ({
            ...state,
            // Every route of the batch sent successfully: the backend has
            // stamped the typed notes onto the events, so leftover drafts
            // would mask them (CodeRabbit). Keep drafts for users outside
            // the batch untouched.
            sendScheduleCrewNotes: !(sentUserIds || []).length
                ? state.sendScheduleCrewNotes
                : Object.fromEntries(
                    Object.entries(state.sendScheduleCrewNotes || {})
                        .filter(([userId]) => !sentUserIds.includes(userId)),
                ),
            sendScheduleBatches: Object.fromEntries(
                Object.entries(state.sendScheduleBatches || {})
                    .filter(([id]) => id !== batchId),
            ),
        })),

        on(DispatchActions.sendScheduleSubmitError, (state, { batchId }): DispatchState => ({
            ...state,
            // Any route failure keeps ALL of this batch's drafts so a retry
            // can still read them.
            sendScheduleBatches: updateSendBatch(state.sendScheduleBatches, batchId, (b) => ({
                ...b, pending: Math.max(0, b.pending - 1), hadError: true,
            })),
            callState: { ...state.callState, sendScheduleSubmit: LoadingState.INIT },
        })),

        // ─────────────────────────────────────────────────────────────────────

        on(
            DispatchActions.editCrewSettingsUserSelected,
            (state, { user }): DispatchState => {

                const selectedUserForRateEdit = { ...defaultCrewSettings };

                if (user) {
                    Object.keys(user).forEach((key) => {
                        const value = (user as any)[key];
                        if (value !== undefined && value !== null) {
                            (selectedUserForRateEdit as any)[key] = value;
                        }
                    });
                }

                return {
                    ...state,
                    isEditRateDialogOpen: true,
                    selectedUserForRateEdit: selectedUserForRateEdit as any,
                }
            },
        ),

        on(
            DispatchActions.startCreateCrewMember,
            (state, { searchName }): DispatchState => {
                const newUser = { ...defaultCrewSettings } as any;

                if (searchName) {
                    const parts = searchName.trim().split(/\s+/);
                    if (parts.length > 0) {
                        newUser.givenName = parts[0];
                        if (parts.length > 1) {
                            newUser.familyName = parts.slice(1).join(' ');
                        }
                    }
                }
                
                return {
                    ...state,
                    isEditRateDialogOpen: true,
                    isCreatingUser: false,
                    selectedUserForRateEdit: newUser,
                };
            }
        ),

        on(
            DispatchActions.updateDraftCrewMember,
            (state, { user }): DispatchState => ({
                ...state,
                selectedUserForRateEdit: {
                    ...state.selectedUserForRateEdit,
                    ...user,
                },
            })
        ),

        on(
            DispatchActions.setCreatingUser,
            (state, { isCreating }): DispatchState => ({
                ...state,
                isCreatingUser: isCreating,
            })
        ),

        on(
            DispatchActions.createCrewMember,
            (state): DispatchState => ({
                ...state,
                isCreatingUser: true,
            })
        ),

        on(
            DispatchActions.createCrewMemberSuccess,
            (state, { user }): DispatchState => ({
                ...state,
                users: [...state.users, user],
                isCreatingUser: false,
                // Merge ID into selected user to preserve drafts typed during creation
                selectedUserForRateEdit: {
                    ...state.selectedUserForRateEdit,
                    id: user.id,
                },
            })
        ),

        on(
            DispatchActions.crewSettingsUpdated,
            (state, update): DispatchState => {
                return {
                    ...state,
                    selectedUserForRateEdit: {
                        ...state.selectedUserForRateEdit,
                        ...update,
                    },
                };
            },
        ),

        on(
            DispatchActions.crewBookOffCreated,
            (state): DispatchState => ({
                ...state
            })
        ),

        on(
            DispatchActions.editCrewSettingsDialogClosed,
            (state): DispatchState => ({
                ...state,
                isEditRateDialogOpen: false,
                selectedUserForRateEdit: null,
            }),
        ),

        on(
            DispatchActions.crewSettingsSavedSuccessfully,
            (state, action): DispatchState => {

                const updatedUsers = state.users.map((user) =>
                    user.id === action.id ? {
                        ...user,
                        ...action as any,
                    } as User : user
                );

                // This user might be assigned to events as a crew, hence update the event attendee as well.
                const selectedUser = state.selectedUserForRateEdit;
                
                const updatedEvents = state.events.map((event) => {
                    const updatedAttendees = event.attendees.map((attendee) =>
                        (selectedUser && attendee.user?.id && attendee.user?.id === state.selectedUserForRateEdit?.id)
                            ? {
                                ...attendee, user: {
                                    ...attendee.user,
                                    rateHourly: state.selectedUserForRateEdit.rateHourly,
                                    shiftAvailability: state.selectedUserForRateEdit.shiftAvailability,
                                }
                            }
                            : attendee
                    );

                    return { ...event, attendees: updatedAttendees };
                });

                return {
                    ...state,
                    users: updatedUsers,
                    events: updatedEvents,
                }
            },
        ),
        on(
            DispatchActions.assignCrewMembersWithAIClicked,
            (state): DispatchState => ({
                ...state,
                assignCrewInfeasibilityReasons: null,
                callState: {
                    ...state.callState,
                    assignCrewWithAI: LoadingState.LOADING,
                },
            }),
        ),
        on(
            DispatchActions.crewMembersAssignedWithAISuccessfully,
            (state, { output: _output }): DispatchState => {
                return {
                    ...state,
                    callState: {
                        ...state.callState,
                        assignCrewWithAI: LoadingState.LOADED,
                    }
                };
            }
        ),
        on(
            DispatchActions.failedToAssignCrewMembersWithAI,
            (state, { error }): DispatchState => {
                return {
                    ...state,
                    callState: {
                        ...state.callState,
                        assignCrewWithAI: {
                            error,
                        },
                    }
                };
            }
        ),
        on(
            DispatchActions.crewAssignmentInfeasible,
            (state, { reasons }): DispatchState => ({
                ...state,
                assignCrewInfeasibilityReasons: reasons,
                callState: {
                    ...state.callState,
                    assignCrewWithAI: {
                        error: new Error('This schedule is not feasible'),
                    },
                },
            }),
        ),
        on(
            DispatchActions.assignCrewResultDismissed,
            (state): DispatchState => ({
                ...state,
                assignCrewInfeasibilityReasons: null,
            }),
        ),
        on(
            DispatchActions.updateCrewBookOffEvents,
            (state, { bookOffEvents }): DispatchState => {
                return {
                    ...state,
                    selectedUserBookOffEvents: bookOffEvents
                    ,
                }
            }
        ),
        on(
            DispatchActions.crewSettingsTabChanged,
            (state, { index }): DispatchState => ({
                ...state,
                selectedCrewSettingsTab: index,
            })
        ),
        on(
            DispatchActions.workerHistoryLoaded,
            (state, { events }): DispatchState => {
                const workerHistory: Record<string, CalendarEventForDispatchFragment[]> = {};
                events.forEach(event => {
                     event.attendees.forEach(att => {
                         const uid = att.user.id;
                         if (!workerHistory[uid]) workerHistory[uid] = [];
                         workerHistory[uid].push(event);
                     });
                });
                return {
                    ...state,
                    workerHistory
                };
            }
        ),
        on(
            DispatchActions.toggleMapRouteVisibility,
            (state, { routeKey }): DispatchState => {
                const isHidden = state.hiddenMapRoutes.includes(routeKey);
                const newHiddenRoutes = isHidden
                    ? state.hiddenMapRoutes.filter(k => k !== routeKey)
                    : [...state.hiddenMapRoutes, routeKey];
                return {
                    ...state,
                    hiddenMapRoutes: newHiddenRoutes
                };
            }
        ),
        on(
            DispatchActions.setHiddenMapRoutes,
            (state, { hiddenRoutes }): DispatchState => ({
                ...state,
                hiddenMapRoutes: hiddenRoutes
            })
        ),
        on(
            DispatchActions.setDockLocation,
            (state, { location }): DispatchState => ({
                ...state,
                dockLocation: location
            })
        ),

        // ── Optimistic placeholder event creation ─────────────────────────────
        // When a reserve/non-billable/ops-manager event is created, we optimistically
        // add a synthetic event to the state so the UI updates instantly. On error,
        // we remove it and hard refresh. On success, the synthetic event stays
        // (it will be replaced by the real one on the next natural refresh).
        on(
            DispatchActions.reserveEventCreated,
            (state, { input }): DispatchState => {
                const event = synthesizePlaceholderEvent(
                    input, RESERVE_EVENT_TYPE, input.description || 'Reserve',
                ) as any;
                return {
                    ...state,
                    events: [...state.events, event],
                };
            },
        ),
        on(
            DispatchActions.nonBillableEventCreated,
            (state, { input }): DispatchState => {
                const event = synthesizePlaceholderEvent(
                    input, NON_BILLABLE_EVENT_TYPE,
                    input.description || `Non-Billable (${input.reason})`,
                ) as any;
                return {
                    ...state,
                    events: [...state.events, event],
                };
            },
        ),
        on(
            DispatchActions.opsManagerEventCreated,
            (state, { input }): DispatchState => {
                const event = synthesizePlaceholderEvent(
                    input, OPS_MANAGER_EVENT_TYPE,
                    input.description || 'Ops Manager',
                ) as any;
                return {
                    ...state,
                    events: [...state.events, event],
                };
            },
        ),
        // On success, the synthetic event already has a UUID matching the server
        // (passed via temporaryId). Just update the fields from the server response.
        on(
            DispatchActions.reserveEventCreateSuccess,
            (state, { calendarEvent }): DispatchState => ({
                ...state,
                events: state.events.map((e) =>
                    e.id === calendarEvent.id ? { ...e, ...calendarEvent } as any : e
                ),
            }),
        ),
        on(
            DispatchActions.nonBillableEventCreateSuccess,
            (state, { calendarEvent }): DispatchState => ({
                ...state,
                events: state.events.map((e) =>
                    e.id === calendarEvent.id ? { ...e, ...calendarEvent } as any : e
                ),
            }),
        ),
        on(
            DispatchActions.opsManagerEventCreateSuccess,
            (state, { calendarEvent }): DispatchState => ({
                ...state,
                events: state.events.map((e) =>
                    e.id === calendarEvent.id ? { ...e, ...calendarEvent } as any : e
                ),
            }),
        ),
        on(
            DispatchActions.reserveEventCreateError,
            DispatchActions.nonBillableEventCreateError,
            DispatchActions.opsManagerEventCreateError,
            (state): DispatchState => ({
                ...state,
                callState: {
                    ...state.callState,
                    events: LoadingState.LOADED,
                },
            }),
        ),
    )
});

/** Local-timezone ISO date (YYYY-MM-DD) for ensure-tracking keyed by day. */
/** Local-timezone ISO date (YYYY-MM-DD) — shared key shape for ensure-tracking. */
export function toLocalIsoDateString(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/**
 * Synthesize a minimal CalendarEventForScheduleFragment-like object for
 * optimistic UI updates when creating placeholder events (reserve, non-billable,
 * ops-manager). The real event from the server will replace this on the next
 * natural refresh.
 */
function synthesizePlaceholderEvent(
    input: { eventId?: string; userId?: string; start: number; end: number; description?: string; metadata?: any },
    type: string,
    title: string,
): Partial<CalendarEventForScheduleFragment> {
    const tempId = input.eventId || uuid();
    return {
        id: tempId,
        title,
        type,
        // OPS tasks default to 'booked' (Ray) so the completed toggle reads
        // booked → completed → booked; everything else confirmed.
        status: type === OPS_TASK_EVENT_TYPE ? 'booked' : 'confirmed',
        start: input.start,
        end: input.end,
        description: input.description || '',
        attendees: input.userId ? [{
            role: EventAttendeeRoles.crewMember,
            user: { id: input.userId } as any,
        } as any] : [],
        assets: [],
        attributes: ['disable-notifications'],
        metadata: input.metadata,
        // Minimal fields required by CalendarEventForScheduleFragment
        discountedSubTotal: 0,
        subTotal: 0,
        taxTotal: 0,
        total: 0,
        order: 0,
        createdAt: Date.now() / 1000,
        updatedAt: Date.now() / 1000,
    } as any;
}
